import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, AlertTriangle, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { useEtapas, useOportunidades } from '../hooks';
import {
  MoveStagePayload,
  OPORTUNIDADES_KEYS,
  OportunidadeListItem,
  OpportunityStage,
  moverEtapa,
} from '../api';
import { OportunidadePrioridadeBadge } from './OportunidadeBadges';
import { formatCurrencyBRL, formatDateBR } from '../utils/format';

const CARD_LIMIT_PER_COLUMN = 100;

/**
 * Kanban interativo — visão alternativa à Lista (US-COM-004 §Filtros e
 * Visões, task 14.2).
 *
 * Estratégia:
 *   1. Busca oportunidades abertas (status='aberta', não rascunho) em
 *      lote único com `limit` alto. Sem virtualização nesta fatia —
 *      trocamos por `@tanstack/react-virtual` quando datasets reais
 *      crescerem além de ~100/coluna
 *   2. Agrupa por `stageId` para renderizar colunas ordenadas por
 *      `order` das etapas ativas com `outcomeType='aberta'`
 *   3. Drag-drop com @dnd-kit: cada card é um Draggable e cada coluna
 *      é um Droppable
 *   4. Optimistic update na cache do useQuery antes do POST; rollback
 *      manual se o backend rejeitar (422 quando pula etapa obrigatória,
 *      margem sem aprovação para "Proposta" etc.)
 *
 * Retrocessos exigem justificativa (≥ 20 chars) no backend. Neste MVP,
 * quando o usuário arrasta para trás pedimos a justificativa via
 * `window.prompt`. Uma UX melhor (Modal de retrocesso) fica para fatia
 * seguinte se preciso.
 */
export const OpportunityKanban: React.FC = () => {
  const { data: stages, isLoading: stagesLoading } = useEtapas();
  const { data, isLoading, isError, error, refetch } = useOportunidades({
    status: 'aberta',
    limit: 1000, // 1 request grande no MVP; refinar com pagination por coluna depois
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Colunas: apenas etapas ativas e "aberta". As etapas de desfecho
  // (ganha/perdida/…) NÃO aparecem — o Kanban só serve para movimentar
  // dentro do pipeline aberto. Desfechos vão via modais dedicados.
  const columns = useMemo<OpportunityStage[]>(
    () =>
      (stages ?? [])
        .filter((s) => s.active && s.outcomeType === 'aberta')
        .sort((a, b) => a.order - b.order),
    [stages],
  );

  const byStageId = useMemo(() => {
    const map = new Map<string, OportunidadeListItem[]>();
    for (const c of columns) map.set(c.id, []);
    for (const o of data?.data ?? []) {
      const arr = map.get(o.stageId);
      if (arr) arr.push(o);
    }
    return map;
  }, [columns, data]);

  const cardById = useMemo(() => {
    const m = new Map<string, OportunidadeListItem>();
    for (const o of data?.data ?? []) m.set(o.id, o);
    return m;
  }, [data]);

  const stageById = useMemo(() => {
    const m = new Map<string, OpportunityStage>();
    for (const s of stages ?? []) m.set(s.id, s);
    return m;
  }, [stages]);

  // useMoverEtapa é `card-bound`; o Kanban precisa mover N cards
  // diferentes numa mesma tela. Usamos um `useMutation` local aqui
  // que aceita `{ cardId, payload }` e faz a invalidação apropriada
  // depois do sucesso ou erro (o Kanban também refetch em qualquer
  // caso p/ garantir sincronismo).
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: ({ cardId, payload }: { cardId: string; payload: MoveStagePayload }) =>
      moverEtapa(cardId, payload),
    onSettled: (_data, _err, { cardId }) => {
      qc.invalidateQueries({ queryKey: OPORTUNIDADES_KEYS.detail(cardId) });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'list'] });
      qc.invalidateQueries({ queryKey: [...OPORTUNIDADES_KEYS.all, 'indicators'] });
    },
  });

  const sensors = useSensors(
    // MouseSensor com activation delay curta pra permitir cliques normais.
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const [optimistic, setOptimistic] = useState<
    { id: string; from: string; to: string } | null
  >(null);

  const onDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
    setMoveError(null);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const cardId = String(e.active.id);
    const targetStageId = e.over ? String(e.over.id) : null;
    if (!targetStageId) return;

    const card = cardById.get(cardId);
    if (!card) return;
    if (card.stageId === targetStageId) return;

    const fromStage = stageById.get(card.stageId);
    const toStage = stageById.get(targetStageId);
    if (!fromStage || !toStage) return;

    // Retrocesso: order menor. Backend exige justificativa ≥ 20 chars.
    let justification: string | undefined;
    if (toStage.order < fromStage.order) {
      const input = window.prompt(
        `Você está movendo "${card.nome}" para uma etapa anterior (${toStage.label}). Justifique a decisão (mín. 20 caracteres):`,
        '',
      );
      if (!input) return;
      if (input.trim().length < 20) {
        setMoveError('Justificativa precisa ter pelo menos 20 caracteres.');
        return;
      }
      justification = input.trim();
    }

    setOptimistic({ id: cardId, from: card.stageId, to: targetStageId });

    try {
      await mut.mutateAsync({
        cardId,
        payload: { toStageId: targetStageId, justification },
      });
    } catch (err) {
      setMoveError(getFriendlyErrorMessage(err, 'Falha ao mover para a nova etapa.'));
    } finally {
      setOptimistic(null);
      // Refetch para reconciliar com a verdade do servidor —
      // useAcaoMutation já invalidou, mas Kanban usa sua própria query
      // e queremos garantir sincronismo mesmo em erro.
      refetch();
    }
  };

  if (stagesLoading || isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto py-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-[85vw] max-w-sm flex-shrink-0 space-y-2 md:w-80">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="error">
        Erro ao carregar oportunidades: {(error as Error)?.message ?? 'Falha desconhecida.'}
      </Alert>
    );
  }

  return (
    <div>
      {moveError && (
        <Alert variant="error" className="mb-3">
          {moveError}
        </Alert>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {/* Container do Kanban:
            - Mobile (<768px): snap-x-mandatory → cada coluna ocupa quase
              a viewport toda e o swipe horizontal "encaixa" na próxima.
              O touch-sensor do @dnd-kit continua permitindo arrastar
              cards entre colunas (long-press + drag).
            - Desktop (>=768px): scroll horizontal contínuo padrão. */}
        <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-4 md:snap-none">
          {columns.map((col) => {
            const cards = byStageId.get(col.id) ?? [];
            const displayed = optimistic
              ? applyOptimistic(cards, col.id, optimistic, cardById)
              : cards;
            return (
              <KanbanColumn
                key={col.id}
                stage={col}
                cards={displayed.slice(0, CARD_LIMIT_PER_COLUMN)}
                overflow={
                  displayed.length > CARD_LIMIT_PER_COLUMN
                    ? displayed.length - CARD_LIMIT_PER_COLUMN
                    : 0
                }
              />
            );
          })}
        </div>
        <DragOverlay>
          {activeId && cardById.get(activeId) && (
            <KanbanCard card={cardById.get(activeId)!} isOverlay />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

/**
 * Aplica a movimentação otimista in-memory: remove o card da coluna de
 * origem e insere na de destino. Não muta os dados da cache — só o
 * layout local, para reduzir "flicker" durante o POST.
 */
function applyOptimistic(
  cards: OportunidadeListItem[],
  columnId: string,
  op: { id: string; from: string; to: string },
  cardById: Map<string, OportunidadeListItem>,
): OportunidadeListItem[] {
  const isSource = columnId === op.from;
  const isTarget = columnId === op.to;
  if (!isSource && !isTarget) return cards;
  if (isSource) return cards.filter((c) => c.id !== op.id);
  const card = cardById.get(op.id);
  if (!card) return cards;
  if (cards.find((c) => c.id === op.id)) return cards;
  return [{ ...card, stageId: op.to }, ...cards];
}

// ---------------------------------------------------------------------
// Column + Card
// ---------------------------------------------------------------------

const KanbanColumn: React.FC<{
  stage: OpportunityStage;
  cards: OportunidadeListItem[];
  overflow: number;
}> = ({ stage, cards, overflow }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const totalWeighted = useMemo(
    () =>
      cards.reduce(
        (acc, c) => acc + (c.valorPonderado ? Number(c.valorPonderado) : 0),
        0,
      ),
    [cards],
  );

  return (
    <div
      ref={setNodeRef}
      // Mobile: coluna ocupa ~85vw + snap-center para "encaixar" no
      // scroll horizontal. Desktop: w-80 fixo (padrão Kanban).
      className={`w-[85vw] max-w-sm flex-shrink-0 snap-center rounded-lg border p-2 transition-colors md:w-80 md:snap-none ${
        isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="mb-2 flex items-center justify-between px-1">
        <div>
          <div className="text-sm font-semibold text-gray-800">{stage.label}</div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{cards.length} oportunidade{cards.length === 1 ? '' : 's'}</span>
            {stage.isRequired && (
              <span className="rounded bg-blue-100 px-1 text-blue-800">Obrigatória</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <TrendingUp className="h-3 w-3" />
            {formatCurrencyBRL(totalWeighted ? String(totalWeighted.toFixed(2)) : null)}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {cards.map((c) => (
          <KanbanCard key={c.id} card={c} />
        ))}
        {overflow > 0 && (
          <p className="rounded border border-dashed border-gray-300 p-2 text-center text-xs text-gray-500">
            +{overflow} oportunidade{overflow === 1 ? '' : 's'} adicional{overflow === 1 ? '' : 'is'} — refine os filtros na visão Lista para ver todas.
          </p>
        )}
        {cards.length === 0 && (
          <p className="rounded border border-dashed border-gray-300 p-3 text-center text-xs text-gray-400">
            Nenhuma oportunidade nesta etapa.
          </p>
        )}
      </div>
    </div>
  );
};

const KanbanCard: React.FC<{ card: OportunidadeListItem; isOverlay?: boolean }> = ({
  card,
  isOverlay,
}) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
  });
  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      {...(isOverlay ? {} : listeners)}
      {...(isOverlay ? {} : attributes)}
      onClick={() => !isOverlay && navigate(`/oportunidades/${card.id}`)}
      className={`cursor-grab rounded-md border bg-white p-2 shadow-sm hover:shadow ${
        isDragging && !isOverlay ? 'opacity-40' : ''
      } ${isOverlay ? 'ring-2 ring-blue-400' : ''}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] text-gray-500">
          {card.codigo ?? 'RASCUNHO'}
        </span>
        <div className="flex flex-shrink-0 items-center gap-1">
          {card.isConfidential && <Lock className="h-3 w-3 text-purple-600" aria-label="Confidencial" />}
          {card.isStalled && <AlertTriangle className="h-3 w-3 text-amber-600" aria-label="Parada" />}
          <OportunidadePrioridadeBadge prioridade={card.prioridade} />
        </div>
      </div>
      <div className="line-clamp-2 text-sm font-medium text-gray-900">{card.nome}</div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500">
        <span className="tabular-nums">{formatCurrencyBRL(card.valorEstimado)}</span>
        <span>{formatDateBR(card.expectedClosingDate)}</span>
      </div>
    </div>
  );
};
